import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { MainLayout } from "@/components/MainLayout";
import { usePortfolio } from "@/context/PortfolioContext";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FileText,
  Download,
  RefreshCw,
  Calendar,
  TrendingUp,
  TrendingDown,
  Loader2,
  FileDown,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportsPage = () => {
  const { formatCurrency } = usePortfolio();
  const { t } = useI18n();
  const { isDemo } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchReports = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/reports`, { withCredentials: true });
      setReports(resp.data || []);
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API_URL}/api/reports/generate`, {}, { withCredentials: true });
      toast.success("Report generated successfully!");
      fetchReports();
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to generate report";
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (reportId, month) => {
    try {
      const resp = await axios.get(`${API_URL}/api/reports/${reportId}/download`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `CablewayFinance_Report_${month || "report"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download report");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "N/A";
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTriggerBadge = (trigger) => {
    if (trigger === "auto_monthly")
      return <Badge variant="outline" className="text-[10px] border-blue-500/30 text-blue-500">Auto</Badge>;
    if (trigger === "manual")
      return <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">Manual</Badge>;
    if (trigger?.startsWith("admin:"))
      return <Badge variant="outline" className="text-[10px] border-orange-500/30 text-orange-500">Admin</Badge>;
    return <Badge variant="outline" className="text-[10px]">{trigger}</Badge>;
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="reports-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">
              {t("reports.title")}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t("reports.sub")}
            </p>
          </div>
          <Button
            onClick={handleGenerate}
            disabled={generating || isDemo}
            data-testid="generate-report-btn"
            className="gap-2"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4" />
            )}
            {generating ? t("reports.generating") : t("reports.generate")}
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold tracking-widest uppercase text-muted-foreground/80 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t("reports.history")}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">{t("reports.noReports")}</p>
                <p className="text-sm mt-1">{t("reports.noReportsSub")}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("reports.date")}</TableHead>
                    <TableHead>{t("reports.month")}</TableHead>
                    <TableHead>{t("reports.type")}</TableHead>
                    <TableHead className="text-right">{t("reports.portfolioValue")}</TableHead>
                    <TableHead className="text-right">{t("reports.pl")}</TableHead>
                    <TableHead className="text-center">{t("reports.assets")}</TableHead>
                    <TableHead className="text-right">{t("reports.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((r) => (
                    <TableRow key={r.report_id} data-testid={`report-row-${r.report_id}`}>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                          {formatDate(r.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm">{r.month}</span>
                      </TableCell>
                      <TableCell>{getTriggerBadge(r.triggered_by)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {formatCurrency(r.total_value || 0)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`font-mono font-semibold flex items-center justify-end gap-1 ${(r.gain_loss || 0) >= 0 ? "text-green-500" : "text-red-500"}`}>
                          {(r.gain_loss || 0) >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                          {formatCurrency(r.gain_loss || 0)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center font-mono">
                        {r.asset_count || 0}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(r.report_id, r.month)}
                          data-testid={`download-report-${r.report_id}`}
                          className="gap-1 text-xs"
                        >
                          <Download className="w-3.5 h-3.5" />
                          PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ReportsPage;
